import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DialogService } from '../shared/components/dialog/dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HttpEventType } from '@angular/common/http';
import { FeatureFlagsService } from '../_services/feature-flags.service';
import { ApiService } from '../_services/api.service';
import { UploadService } from '../_services/upload.service';
import { AuthService } from '../_services/auth.service';
import { HasUnsavedChanges } from '../_guards/unsaved-changes.guard';
import { ConfirmComponent } from '../shared/dialogs/confirm/confirm.component';
import { SelectTemplateComponent } from '../shared/dialogs/select-template/select-template.component';
import {
  UploadSelectPatientComponent,
  UploadSelectPatientResult
} from './select-patient/upload-select-patient.component';
import { Dictation } from '../_interfaces/dictation';
import { environment } from '../../environments/environment';
import {
  SUPPORTED_AUDIO_EXTENSIONS,
  UploadValidationError,
  validateAudioSelection,
  getFileExtension
} from './upload-file-validation';
import {
  UploadErrorDialogComponent,
  UploadErrorDialogData,
  UploadErrorDialogResult
} from './upload-error-dialog/upload-error-dialog.component';
import { isUploaded } from './upload-status';
import { AnalyticsService } from '../_services/analytics.service';

const ERROR_DIALOG_CONFIG: Record<UploadValidationError, UploadErrorDialogData> = {
  format: {
    titleKey: 'File format not compatible',
    messageKeys: [
      "We're sorry, we are unable to accept this file type. We can accept WAV, MP3, M4A, WMV, CAF, OGG, OPUS file types",
      'Please select a different file and try again'
    ],
    cancelKey: 'Cancel',
    actionKey: 'Select another file'
  },
  size: {
    titleKey: 'File too large',
    messageKeys: [
      "We're sorry, the file selected exceeds the limit. Please select a different file and try again",
      'WAV, MP3, M4A, WMV, CAF, OGG, OPUS: Max 500 MB / 3h; DSS, DS2: Max 250 MB / 3h'
    ],
    cancelKey: 'Cancel',
    actionKey: 'Select another file'
  },
  multiple: {
    titleKey: 'Only one file allowed',
    messageKeys: [
      "We're sorry, we can only accept one file. Please select a single file and try again"
    ],
    cancelKey: 'Cancel',
    actionKey: 'Select one file'
  }
};

interface UploadSession {
  audioUploadUrl: string;
  fileName: string;
  format: string;
  expiresAt: number;
  uploadComplete?: boolean;
}

@Component({
  selector: 'app-upload',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
  host: {
    class: 'block h-full p-4 md:px-10 md:pt-8 md:pb-14'
  }
})
export class UploadComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  private readonly analytics = inject(AnalyticsService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly acceptAttr = SUPPORTED_AUDIO_EXTENSIONS.map((ext) => `.${ext}`).join(',');

  isDragging = false;
  private dragDepth = 0;

  state: 'idle' | 'uploading' | 'processing'  | 'complete' = 'idle';
  processingTakingLong = false;
  selectedFile: File | null = null;
  uploadProgress = 0;
  private uploadSubscription: Subscription | null = null;
  private pollTimeout: ReturnType<typeof setTimeout> | null = null;
  private dictationId: string | null = null;
  private storedFileName: string | null = null;
  private lastKnownPollStatus = 'pending';

  selectionStage: 'none' | 'patient' | 'template' = 'none';
  private dictation: Dictation | null = null;
  private destroyed = false;

  constructor(
    private flags: FeatureFlagsService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: DialogService,
    private apiService: ApiService,
    private uploadService: UploadService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (!this.flags.isEnabled('uploadAudio')) {
      this.router.navigate(['/home']);
      return;
    }

    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId) {
      void this.loadDictationFromRoute(routeId).catch((error) => {
        console.error('Failed to initialise upload route:', error);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    this.stopPolling();
    this.dialog.closeAll();

    // Leaving mid-upload still cleans up the orphaned dictation. Processing is
    // left running on the backend so the user can return via /upload/:id.
    if (this.dictationId && this.state === 'uploading') {
      const idToDelete = this.dictationId;
      this.dictationId = null;
      this.apiService.deleteDictation(idToDelete).catch((error) => {
        console.error('Failed to delete dictation on navigation away:', error);
      });
    }
  }

  get displayFileName(): string {
    return this.selectedFile?.name ?? this.storedFileName ?? this.translate.instant('Audio file');
  }

  openFilePicker(): void {
    this.fileInput.nativeElement.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFiles(input.files);
    input.value = '';
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth++;
    this.isDragging = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.isDragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.isDragging = false;
    this.handleFiles(event.dataTransfer?.files ?? null);
  }

  @HostListener('document:dragover', ['$event'])
  onDocumentDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  @HostListener('document:drop', ['$event'])
  onDocumentDrop(event: DragEvent): void {
    event.preventDefault();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  @HostListener('window:offline')
  onBrowserOffline(): void {
    if (this.state === 'processing') {
      this.dialog.open(ConfirmComponent, {
        data: {
          title: this.translate.instant('You are offline'),
          description: this.translate.instant(
            'Processing continues in the background - the status will update when your connection is restored'
          ),
          confirmText: this.translate.instant('Okay'),
          compactActions: true
        },
        presentation: 'dialog',
        autoFocus: false,
        restoreFocus: false
      });
    }
  }

  @HostListener('window:online')
  onBrowserOnline(): void {
    if (this.state === 'processing' && this.dictationId) {
      void this.checkProcessingStatus();
    }
  }

  hasUnsavedChanges(): boolean {
    return this.state === 'uploading'
  }

  private handleFiles(files: FileList | null): void {
    if (!files || files.length === 0 || this.state === 'uploading') {
      return;
    }

    const result = validateAudioSelection(files);

    if (result.valid && result.file) {
      this.onValidFile(result.file);
      return;
    }

    if (result.error) {
      this.openErrorDialog(result.error);
    }
  }

  private onValidFile(file: File): void {
    this.selectedFile = file;
    void this.startUpload();
  }

  onBreadcrumbHome(): void {
    this.analytics.track('breadcrumb_clicked', { from_page: 'upload', to_page: 'home' });
  }

  clearSelection(): void {
    if (this.state === 'uploading') return;
    this.selectedFile = null;
    this.state = 'idle';
  }

  async startUpload(): Promise<void> {
    if (!this.selectedFile || this.state === 'uploading') {
      return;
    }

    const file = this.selectedFile;
    const extension = getFileExtension(file.name);
    this.analytics.track('audio_upload_started');

    try {
      const user = this.authService.getUser();

      const dictationPayload: any = {
        application: 'Copilot',
        author: user?.settings.nlpUser || '',
        engine: user?.entity.settings.vr?.nlp_backend_engine_id || '',
        language: user?.entity.settings.vr?.nlp_backend_language_id || '',
        topic: user?.entity.settings.vr?.nlp_backend_topic_id || '',
        type: 'backend',
        version: environment.version,
        audioUrl: 'upload',
        format: extension
      };

      const createRes = await this.apiService.createDictation(dictationPayload);
      const dictationId = createRes._id;
      if (!dictationId) {
        throw new Error('Dictation id not received from server.');
      }

      const audioUploadUrl = createRes.audioUploadUrl;
      if (!audioUploadUrl || typeof audioUploadUrl !== 'string') {
        throw new Error('Upload URL not received from server.');
      }

      void this.persistUploadFilename(dictationId, file.name);
      this.saveUploadSession(dictationId, {
        audioUploadUrl,
        fileName: file.name,
        format: extension,
        expiresAt: Date.now() + 14 * 60 * 1000
      });

      // Keep the file in memory across navigation; upload starts on /upload/:id.
      this.uploadService.setPendingUpload(dictationId, file, audioUploadUrl);
      await this.router.navigate(['/upload', dictationId], { replaceUrl: true });
    } catch (error) {
      this.onUploadError(error);
    }
  }

  private beginFileUpload(audioUploadUrl: string, dictationId: string): void {
    if (!this.selectedFile) return;

    this.uploadSubscription = this.uploadService.uploadToPresignedUrl(audioUploadUrl, this.selectedFile).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round((100 * event.loaded) / (event.total || 1));
        } else if (event.type === HttpEventType.Response) {
          this.uploadProgress = 100;
          this.markUploadComplete(dictationId);
          this.onUploadSuccess(dictationId);
        }
      },
      error: (error) => {
        this.onUploadError(error);
      }
    });
  }

  private async loadDictationFromRoute(id: string): Promise<void> {
    try {
      const dictation = await this.apiService.getDictation(id);

      if (dictation.type !== 'backend') {
        this.redirectToUpload('Dictation not found');
        return;
      }

      this.dictationId = id;
      this.storedFileName = dictation.copilot?.upload_filename ?? null;
      this.applyDictationStatus(dictation);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.redirectToUpload('Dictation not found');
        return;
      }
      console.error('Failed to load dictation for upload resume:', error);
      this.redirectToUpload('Dictation not found');
    }
  }

  private applyDictationStatus(dictation: Dictation): void {
    const status = dictation.status;
    this.lastKnownPollStatus = status;

    if (status === 'editing' || status === 'done' || status === 'completed') {
      void this.router.navigate(['/document', dictation._id]);
      return;
    }

    if (status === 'processing') {
      this.setProcessingState();
      this.startPolling(dictation._id, status);
      return;
    }

    if (status === 'pending') {
      const pending = this.uploadService.consumePendingUpload(dictation._id);
      if (pending) {
        this.selectedFile = pending.file;
        this.storedFileName = pending.file.name;
        this.state = 'uploading';
        this.uploadProgress = 0;
        this.beginFileUpload(pending.audioUploadUrl, dictation._id);
        return;
      }

      const session = this.dictationId ? this.loadUploadSession(this.dictationId) : null;
      if (session?.uploadComplete || isUploaded(dictation)) {
        this.setProcessingState();
        this.startPolling(dictation._id, status);
        return;
      }

      this.redirectToUpload()
    }

    if (status === 'error' || status === 'failed') {
      this.onProcessingError(new Error('Audio processing failed on the backend.'));
    }
  }

  private async checkProcessingStatus(): Promise<void> {
    if (!this.dictationId || this.state !== 'processing') {
      return;
    }

    try {
      const dictation = await this.apiService.getDictation(this.dictationId);
      if (this.dictationId !== dictation._id || this.state !== 'processing') {
        return;
      }
      this.resolveProcessingStatus(dictation);
    } catch (error) {
      console.error('Failed to check dictation status after reconnect:', error);
    }
  }

  private resolveProcessingStatus(dictation: Dictation): void {
    const status = dictation.status;
    this.lastKnownPollStatus = status;

    if (status === 'editing' || status === 'done' || status === 'completed') {
      void this.onProcessingSuccess();
    } else if (status === 'error' || status === 'failed') {
      this.onProcessingError(new Error('Audio processing failed on the backend.'));
    } else {
      this.stopPolling();
      this.pollStatus(dictation._id, status);
    }
  }

  private onUploadSuccess(dictationId: string): void {
    this.setProcessingState();
    this.startPolling(dictationId, 'pending');
  }

  private onUploadError(error: any): void {
    console.error('Upload failed:', error);
    this.uploadSubscription = null;
    if (this.dictationId) {
      this.uploadService.clearPendingUpload(this.dictationId);
    }

    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: this.translate.instant('Unable to upload'),
        description: this.translate.instant("We're sorry, there was an error while uploading your file"),
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Retry'),
        confirmId: 'Retry_Failed_Upload',
        confirmTestId: 'Retry_Failed_Upload',
        cancelId: 'Exit_Failed_Upload',
        cancelTestId: 'Exit_Failed_Upload'
      },
      size: 'md',
      presentation: 'dialog',
      disableClose: true,
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((retry) => {
      if (this.destroyed) return;
      if (retry) {
        void this.retryUpload();
      } else {
        void this.exitToHome();
      }
    });
  }

  private async retryUpload(): Promise<void> {
    const failedId = this.dictationId;
    this.dictationId = null;
    if (failedId) {
      this.clearUploadSession(failedId);
      this.uploadService.clearPendingUpload(failedId);
      try {
        await this.apiService.deleteDictation(failedId);
      } catch (deleteError) {
        console.error('Failed to delete dictation before retrying upload:', deleteError);
      }
    }

    if (this.destroyed || !this.selectedFile) return;
    this.state = 'idle';
    void this.startUpload();
  }

  private async exitToHome(): Promise<void> {
    this.stopPolling();
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }

    const idToDelete = this.dictationId;
    this.state = 'idle';
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.dictationId = null;

    if (idToDelete) {
      this.clearUploadSession(idToDelete);
      this.uploadService.clearPendingUpload(idToDelete);
    }

    await this.router.navigate(['/home']);

    if (idToDelete) {
      try {
        await this.apiService.deleteDictation(idToDelete);
      } catch (error) {
        console.error('Failed to delete dictation on cancellation:', error);
      }
    }
  }

  private startPolling(dictationId: string, lastStatus = 'pending'): void {
    this.stopPolling();
    this.lastKnownPollStatus = lastStatus;
    void this.pollStatus(dictationId, lastStatus);
  }

  private async pollStatus(dictationId: string, lastStatus: string): Promise<void> {
    let dictation: Dictation | null = null;
    try {
      dictation = await this.apiService.waitForDictationStatusChange(dictationId, lastStatus);
    } catch (error) {
      if (this.dictationId === dictationId && this.state === 'processing') {
        this.pollTimeout = setTimeout(() => this.pollStatus(dictationId, lastStatus), 1000);
      }
      return;
    }

    if (this.dictationId !== dictationId || this.state !== 'processing') {
      return;
    }

    if (!dictation) {
      this.pollStatus(dictationId, lastStatus);
      return;
    }

    this.resolveProcessingStatus(dictation);
  }

  private setProcessingState(): void {
    this.state = 'processing';
    this.processingTakingLong = false;
    setTimeout(() => {
      if (this.state !== 'processing') return;
      this.processingTakingLong = true;
    }, 45_000);
  }

  private stopPolling(): void {
    if (this.pollTimeout) {
      clearTimeout(this.pollTimeout);
      this.pollTimeout = null;
    }
  }

  private async onProcessingSuccess(): Promise<void> {
    this.stopPolling();
    this.state = 'complete';
    this.uploadProgress = 100;
    if (this.dictationId) {
      this.clearUploadSession(this.dictationId);
    }
    this.analytics.track('audio_upload_completed', {
      encounter_id: this.dictationId ?? undefined,
    });
    if (this.destroyed) return;
    this.openPatientSelection();
  }

  private async persistUploadFilename(dictationId: string, fileName: string): Promise<void> {
    this.storedFileName = fileName;
    try {
      await this.apiService.updateDictationCopilot(dictationId, {
        copilot: { type: 'upload', upload_filename: fileName }
      });
    } catch (error) {
      console.error('Failed to persist upload filename:', error);
    }
  }

  private openPatientSelection(): void {
    if (!this.dictationId) return;
    this.selectionStage = 'patient';
    const dialogRef = this.dialog.open<
      UploadSelectPatientComponent,
      { dictationId: string; fileName?: string },
      UploadSelectPatientResult
    >(UploadSelectPatientComponent, {
      data: { dictationId: this.dictationId, fileName: this.displayFileName },
      size: 'lg',
      maxHeight: '100vh',
      disableClose: true,
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((result: UploadSelectPatientResult | undefined) => {
      if (this.destroyed) return;
      if (result?.proceed) {
        void this.advanceToTemplateSelection();
      } else {
        void this.leaveFlow('patient');
      }
    });
  }

  private async advanceToTemplateSelection(): Promise<void> {
    if (!this.dictationId) return;
    try {
      this.dictation = await this.apiService.getDictation(this.dictationId);
    } catch (error) {
      console.error('Failed to load dictation for template selection:', error);
    }
    if (this.destroyed) return;
    this.openTemplateSelection();
  }

  private openTemplateSelection(): void {
    this.selectionStage = 'template';
    const dialogRef = this.dialog.open(SelectTemplateComponent, {
      data: { dictation: this.dictation },
      size: 'mdPlus',
      disableClose: true,
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((templates: unknown) => {
      if (this.destroyed) return;
      if (templates) {
        this.selectionStage = 'none';
        void this.router.navigate(['/document', this.dictationId]);
      } else {
        void this.leaveFlow('template');
      }
    });
  }

  private async leaveFlow(stage: 'patient' | 'template'): Promise<void> {
    const navigated = await this.router.navigate(['/home']);
    if (this.destroyed || navigated) return;
    if (stage === 'patient') {
      this.openPatientSelection();
    } else {
      this.openTemplateSelection();
    }
  }

  private onProcessingError(error: any): void {
    console.error('Processing failed:', error);
    this.stopPolling();

    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: this.translate.instant('Unable to process file'),
        description: this.translate.instant("We're sorry, there was an error while processing your file"),
        cancelText: this.translate.instant('Cancel'),
        confirmText: this.translate.instant('Retry'),
        confirmId: 'Retry_Failed_Processing',
        confirmTestId: 'Retry_Failed_Processing',
        cancelId: 'Exit_Failed_Processing',
        cancelTestId: 'Exit_Failed_Processing'
      },
      size: 'md',
      presentation: 'dialog',
      disableClose: true,
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((retry) => {
      if (this.destroyed) return;
      if (retry) {
        void this.retryUpload();
      } else {
        void this.exitToHome();
      }
    });
  }

  confirmCancelProcessing(): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: this.translate.instant('Cancel audio processing?'),
        description: this.translate.instant('Are you sure you want to cancel the processing of this audio file?'),
        cancelText: this.translate.instant('No, continue'),
        confirmText: this.translate.instant('Yes, cancel'),
        confirmId: 'Confirm_Processing_Cancel',
        confirmTestId: 'Confirm_Processing_Cancel',
        cancelId: 'Continue_Processing',
        cancelTestId: 'Continue_Processing'
      },
      size: 'md',
      presentation: 'dialog',
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        void this.cancelProcessing();
      }
    });
  }

  async cancelProcessing(): Promise<void> {
    this.stopPolling();
    const idToDelete = this.dictationId;

    this.state = 'idle';
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.dictationId = null;

    if (idToDelete) {
      this.clearUploadSession(idToDelete);
      this.uploadService.clearPendingUpload(idToDelete);
      try {
        await this.apiService.deleteDictation(idToDelete);
        this.snackBar.open(
          this.translate.instant('Upload and processing cancelled'),
          this.translate.instant('Close'),
          { duration: 2000 }
        );
      } catch (error) {
        console.error('Failed to delete dictation on cancellation:', error);
      }
    }

    await this.router.navigate(['/upload']);
  }

  confirmCancelUpload(): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: this.translate.instant('Cancel upload'),
        description: this.translate.instant('Are you sure you want to cancel the upload of this audio file?'),
        cancelText: this.translate.instant('No, continue'),
        confirmText: this.translate.instant('Yes, cancel'),
        confirmId: 'Confirm_Upload_Cancel',
        confirmTestId: 'Confirm_Upload_Cancel',
        cancelId: 'Continue_Upload',
        cancelTestId: 'Continue_Upload'
      },
      size: 'md',
      presentation: 'dialog',
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        void this.cancelUpload();
      }
    });
  }

  async cancelUpload(): Promise<void> {
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
      this.uploadSubscription = null;
    }

    const idToDelete = this.dictationId;

    this.state = 'idle';
    this.selectedFile = null;
    this.uploadProgress = 0;
    this.dictationId = null;

    this.snackBar.open(
      this.translate.instant('Upload cancelled'),
      this.translate.instant('Close'),
      { duration: 2000 }
    );

    if (idToDelete) {
      this.clearUploadSession(idToDelete);
      this.uploadService.clearPendingUpload(idToDelete);
      try {
        await this.apiService.deleteDictation(idToDelete);
      } catch (error) {
        console.error('Failed to delete dictation on cancellation:', error);
      }
    }

    await this.router.navigate(['/upload']);
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private redirectToUpload(message?: string): void {
    if (message) {
      this.snackBar.open(
        this.translate.instant(message),
        this.translate.instant('Close'),
        { duration: 3000 }
      );
    }
    void this.router.navigate(['/upload']);
  }

  private uploadSessionKey(dictationId: string): string {
    return `copilot-upload-${dictationId}`;
  }

  private saveUploadSession(dictationId: string, session: UploadSession): void {
    try {
      sessionStorage.setItem(this.uploadSessionKey(dictationId), JSON.stringify(session));
    } catch (error) {
      console.error('Failed to persist upload session:', error);
    }
  }

  private loadUploadSession(dictationId: string): UploadSession | null {
    try {
      const raw = sessionStorage.getItem(this.uploadSessionKey(dictationId));
      return raw ? (JSON.parse(raw) as UploadSession) : null;
    } catch {
      return null;
    }
  }

  private markUploadComplete(dictationId: string): void {
    const session = this.loadUploadSession(dictationId);
    if (session) {
      this.saveUploadSession(dictationId, { ...session, uploadComplete: true });
    }
    void this.persistUploadedAt(dictationId);
  }

  private async persistUploadedAt(dictationId: string): Promise<void> {
    try {
      await this.apiService.updateDictationCopilot(dictationId, {
        copilot: { uploaded_at: new Date().toISOString() }
      });
    } catch (error) {
      console.error('Failed to persist uploaded_at:', error);
    }
  }

  private clearUploadSession(dictationId: string): void {
    try {
      sessionStorage.removeItem(this.uploadSessionKey(dictationId));
    } catch (error) {
      console.error('Failed to clear upload session:', error);
    }
  }

  private openErrorDialog(error: UploadValidationError): void {
    const dialogRef = this.dialog.open<
      UploadErrorDialogComponent,
      UploadErrorDialogData,
      UploadErrorDialogResult
    >(UploadErrorDialogComponent, {
      data: ERROR_DIALOG_CONFIG[error],
      size: 'sm',
      presentation: 'dialog',
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'reselect') {
        this.openFilePicker();
      }
    });
  }
}
